import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useField } from 'formik';
import { Icon } from '../';
import { Client } from '../../models';
import { RootState, useAppSelector, useAppDispatch, loadClients } from '../../redux';
import _ from 'lodash';

interface ClientSelectFieldProps {
    name: string;
    multiple?: boolean;
    placeholder?: string;
    className?: string;
    selectedClassName?: string;
    searchInputClassName?: string;
    resultsClassName?: string;
    resultItemClassName?: string;
    onChange?: (value?: Client) => void;
    value?: Client;
    allowClear?: boolean;
    autoFocus?: boolean;
    /** Render results in document flow (avoids clipping inside overflow:auto modals). */
    resultsInFlow?: boolean;
};

/** Safe label for Client instances or plain/API payloads. */
const clientLabel = (client: Client | AnyObj | string | null | undefined): string => {
    if (!client)
        return '';
    if (_.isString(client))
        return client;
    if (typeof client.name === 'function')
        return client.name();
    if (typeof client.fullname === 'function')
        return client.fullname() || client.company || '';
    return [client.id, client.company, client.firstname, client.lastname]
        .filter(Boolean)
        .join(' - ');
};

const ClientSelectField = (props: ClientSelectFieldProps) => {
    const {
        name, multiple, placeholder, className, selectedClassName,
        searchInputClassName, resultsClassName, resultItemClassName,
        onChange, allowClear = true, autoFocus = false, resultsInFlow = false,
    } = props;

    const [search, setSearch] = useState("");
    const searchInputRef = React.useRef<HTMLInputElement>(null);
    // Firefox can deliver the Enter that opened a modal to a freshly focused input,
    // and/or autofill the field on focus — ignore Enter briefly after autofocus.
    const ignoreEnterUntilRef = React.useRef(0);

    const [ field, meta, helpers ] = useField(name);
    const { value }                = meta;
    const { setValue }             = helpers;

    const dispatch    = useAppDispatch();
    const { t }       = useTranslation();
    const { clients } = useAppSelector((state: RootState) => state.clients);

    useEffect(() => {
        if (!clients?.length)
            dispatch(loadClients());
    }, [dispatch]);

    useEffect(() => {
        if (!autoFocus)
            return;
        ignoreEnterUntilRef.current = Date.now() + 400;
        const timer = window.setTimeout(() => {
            searchInputRef.current?.focus();
        }, 50);
        return () => window.clearTimeout(timer);
    }, [autoFocus]);

    useEffect(() => {
        if (props.value && value && props.value._id !== value._id) {
            setValue(props.value);
        } else if (!value && props.value) {
            setValue(props.value);
        }
    }, [props.value, value, setValue]);

    const addClient = (client: Client) => {
        setSearch("");
        if (!multiple) {
            onChange && onChange(client);
            return setValue(client);
        }

        const newValue = _.clone(value || []);
        newValue.push(client);
        setValue(newValue);
        onChange && onChange(newValue);
    };

    const removeClient = (client: Client) => {
        if (!multiple) {
            onChange && onChange();
            return setValue(null);
        }

        const newValue = _.clone(value).filter((existing: Client) => existing._id !== client._id);
        setValue(newValue);
        onChange && onChange(newValue);
    };

    const results = React.useMemo(() => {
        return search?.length < 3 ? [] : (clients || []).filter((client: Client) => {
            if (!multiple && value?._id === client._id)
                return false;
            if (multiple && value?.find((existing: Client) => existing._id === client._id))
                return false;
            return clientLabel(client).toLowerCase().includes(search.toLowerCase());
        }).sort((a: Client, b: Client) => a.id - b.id);
    }, [search, clients, value, multiple]);

    const handleKeyPress = (e: AnyObj) => {
        if (e.key === 'Enter' || e.code === 'Enter') {
            e.preventDefault();
            e.stopPropagation();
            if (Date.now() < ignoreEnterUntilRef.current)
                return;
            if (results?.length > 0)
                addClient(results[0]);
        } else if (e.key === 'Escape' || e.code === 'Escape') {
            setSearch("");
        }
    };

    return (
        <div className={`flex flex-col w-full ${className || ''}`}>
            {((multiple && value?.length > 0) || (!multiple && value)) && (
                <div className="">
                    {multiple && (value?.length > 0 || placeholder) && (
                        <div className="flex flex-col space-y-1 border p-2 rounded-md">
                            {value?.length === 0 && (
                                <span className="text-sm text-gray-400">{placeholder}</span>
                            )}
                            {value?.map((client: Client) => (
                                <div key={client._id} color="yellow" className={`h-fit font-semibold bg-yellow-100 text-yellow-800 dark:bg-yellow-200 dark:text-yellow-900 group-hover:bg-yellow-200 dark:group-hover:bg-yellow-300 rounded p-1 text-xs w-full ${selectedClassName}`}>
                                    <div className="flex flex-row justify-between items-center gap-2">
                                        <div className="flex-1 min-w-0">
                                            {clientLabel(client)}
                                        </div>
                                        <div
                                            onClick={() => removeClient(client)}
                                            className="cursor-pointer shrink-0 p-1 -m-1"
                                            role="button"
                                            aria-label="Remove"
                                        >
                                            <Icon type="close" color="red-600" />
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                    {!multiple && (value || placeholder) && (
                        <div className="flex flex-col space-y-1 border p-2 rounded-md">
                            {!value ? (
                                <span className="text-sm text-gray-400">{placeholder}</span>
                            ) : (
                                <div key={value?._id} color="yellow" className={`h-fit font-semibold bg-yellow-100 text-yellow-800 dark:bg-yellow-200 dark:text-yellow-900 group-hover:bg-yellow-200 dark:group-hover:bg-yellow-300 rounded p-1 text-xs w-full ${selectedClassName}`}>
                                    <div className="flex flex-row justify-between items-center gap-2">
                                        <div className="flex-1 min-w-0">
                                            {clientLabel(value)}
                                        </div>
                                        {allowClear && (
                                            <div
                                                onClick={() => removeClient(value)}
                                                className="cursor-pointer shrink-0 p-1 -m-1"
                                                role="button"
                                                aria-label="Remove"
                                            >
                                                <Icon type="close" color="red-600" />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
            {(multiple || !value) && (
                <div className="relative">
                    <input
                        ref={searchInputRef}
                        type="text"
                        name={`${name}-client-search`}
                        value={search}
                        onChange={(e: AnyObj) => setSearch(e.target.value)}
                        onKeyDown={handleKeyPress}
                        placeholder={t('projects.search_client') || ''}
                        autoComplete="off"
                        autoCorrect="off"
                        spellCheck={false}
                        className={`block w-full rounded-md border-0 py-1.5 pr-10 text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 ${searchInputClassName}`}
                    />
                    {search?.length > 0 ? (
                        <span className="absolute top-2 right-2 cursor-pointer" onClick={() => setSearch("")}>
                            <Icon type="close" size={5} color="red-600" />
                        </span>
                    ) : (
                        <Icon type="search" className="absolute top-2 right-2" size={5} color="gray-300" />
                    )}
                    {results?.length > 0 && (
                        <div
                            className={`${
                                resultsInFlow
                                    ? 'relative mt-1'
                                    : 'absolute left-0 right-0 top-full z-50 mt-1'
                            } max-h-[20rem] overflow-y-auto rounded-md border shadow-lg ${
                                resultsClassName
                                    || 'border-gray-200 bg-white text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100'
                            }`}
                            data-testid="client-select-results"
                        >
                            {results?.map((client: Client) => (
                                <div
                                    key={client._id}
                                    onClick={() => addClient(client)}
                                    className={resultItemClassName || 'border-b border-gray-100 p-2 hover:bg-blue-100 dark:border-gray-700 dark:hover:bg-blue-900/40 cursor-pointer text-sm text-gray-900 dark:text-gray-100'}
                                >
                                    {clientLabel(client)}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default ClientSelectField;
